// OrderSelectComponent.tsx
import { Autocomplete, TextField } from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { useState, useEffect } from "react"
import { apiFetch } from "@/hooks/apiFetch"

type OrderOption = Record<string, any>

type OrderSelectComponentProps = {
  placeHolder?: string
  isError?: boolean
  errorMessage?: string
  /** The value (id) selected for the input. */
  value?: string | number | null
  /** Called when selected value changes; returns the id (optionValue) or null */
  onChange?: (value: string | number | null) => void
  optionLabel?: string
  optionValue?: string
}

export default function OrderSelectComponent({
  placeHolder = "Order Number",
  isError = false,
  value,
  onChange,
  optionLabel = "code",
  optionValue = "id",
  errorMessage = "",
}: OrderSelectComponentProps) {
  const [inputValue, setInputValue] = useState("")
  const [searchTerm, setSearchTerm] = useState("")

  // Update search term whenever input changes (no debounce for now)
  useEffect(() => {
    setSearchTerm(inputValue)
  }, [inputValue])

  const { data: paginatedResponse, isLoading } = useQuery<any>({
    queryKey: ["orders", searchTerm],
    queryFn: async () =>
      await apiFetch(`order?search=${encodeURIComponent(searchTerm)}`),
    enabled: true,
    staleTime: 5 * 60 * 1000,
  })

  // Use optional chaining, and treat options as an array of "OrderOption"
  const options: OrderOption[] = paginatedResponse?.data?.data ?? []

  const selectedOption: OrderOption | null =
    options.find((opt: OrderOption) => opt[optionValue] === value) ?? null

  return (
    <Autocomplete<OrderOption, false, false, false>
      options={options}
      getOptionLabel={(option: OrderOption) =>
        String(option?.[optionLabel] ?? "")
      }
      isOptionEqualToValue={(option: OrderOption, val: OrderOption | null) =>
        Boolean(option?.[optionValue] === val?.[optionValue])
      }
      value={selectedOption}
      loading={isLoading}
      onChange={(_, newValue) => {
        onChange?.(newValue ? newValue[optionValue] : null)
      }}
      onInputChange={(_, val, reason) => {
        if (reason === "input") {
          setInputValue(val)
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={placeHolder}
          error={isError}
          helperText={errorMessage}
        />
      )}
    />
  )
}
